import React from 'react';
import moment from 'moment';
import { Table } from 'semantic-ui-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Heading from '../../components/Heading';
import Pagination from '../../components/Pagination';
import CountDisplay from '../../components/CountDisplay';
import InputBox from '../../components/InputBox';
import Button from '../../components/Button';
import Loader from '../../components/Loader';
import fetchAllJobs from '../../services/fetchAllJobs';
import fetchJobsCount from '../../services/fetchJobsCount';
import reindexJobs from '../../services/reindexJobs';
import user from '../../store/user';
import { adminFeatureMapping } from '../../models/adminFeatureMapping';

import './styles.scss';

class JobsReindex extends React.Component {
    state = {
        recruiterId: '',
        companyId: '',
        publishId: '',
        domain: '',
        jobs: [],
        totalCount: 0,
        pageNumber: 1,
        pageContent: 50,
        isLoading: false,
        bulkLoading: false,
        adminId: null,
        selectedMap: {},
        isSelectAll: false,
        rowLoading: {}
    };

    async componentDidMount() {
        const profile = await user.getProfile();
        if (!user.checkFeatureAccess(adminFeatureMapping.JOBS_LIST)) {
            window.location.href = '/notAdmin';
            return;
        }
        this.setState({
            adminId: profile.id
        }, this.fetchData);
    }

    getFilters = () => {
        const params = {
            type: 'published',
            pageNumber: this.state.pageNumber,
            pageContent: this.state.pageContent
        };

        const recruiterId = Number(this.state.recruiterId);
        if (Number.isInteger(recruiterId) && recruiterId > 0) {
            params.recruiterId = recruiterId;
        }

        const companyId = Number(this.state.companyId);
        if (Number.isInteger(companyId) && companyId > 0) {
            params.companyId = companyId;
        }

        const publishId = Number(this.state.publishId);
        if (Number.isInteger(publishId) && publishId > 0) {
            params.publishId = publishId;
        }

        if (this.state.domain) {
            params.domain = this.state.domain
                .split(/[\s,]+/)
                .map((item) => item.trim())
                .filter((item) => item)
                .join(',');
        }

        return params;
    }

    isEligible = (job) => {
        if (!job || !job.publishedDate) return false;
        return moment(job.publishedDate).isSameOrAfter(moment().subtract(5, 'months'));
    }

    fetchData = async () => {
        if (!this.state.adminId) return;
        this.setState({ isLoading: true });
        try {
            const params = this.getFilters();
            const countParams = { ...params };
            delete countParams.pageNumber;
            delete countParams.pageContent;

            const [countResp, jobsResp] = await Promise.all([
                fetchJobsCount(this.state.adminId, countParams),
                fetchAllJobs(this.state.adminId, params)
            ]);

            this.setState({
                totalCount: countResp.data || 0,
                jobs: jobsResp.data || [],
                isLoading: false,
                selectedMap: {},
                isSelectAll: false
            });
        } catch (error) {
            this.setState({ isLoading: false });
            toast.error('Failed to fetch jobs');
        }
    }

    onChange = (event) => {
        const { name, value } = event.target;
        this.setState({
            [name]: value
        });
    }

    onSearch = () => {
        this.setState({
            pageNumber: 1
        }, this.fetchData);
    }

    onClear = () => {
        this.setState({
            recruiterId: '',
            companyId: '',
            publishId: '',
            domain: '',
            pageNumber: 1
        }, this.fetchData);
    }

    onPageChange = (pageNumber) => {
        this.setState({ pageNumber }, this.fetchData);
    }

    onToggleSelectAll = (event) => {
        const checked = event.target.checked;
        const selectedMap = {};
        if (checked) {
            this.state.jobs.forEach((job) => {
                if (this.isEligible(job)) {
                    selectedMap[job.publishId] = true;
                }
            });
        }
        this.setState({
            isSelectAll: checked,
            selectedMap
        });
    }

    onToggleSelection = (publishId, checked) => {
        const selectedMap = {
            ...this.state.selectedMap,
            [publishId]: checked
        };

        if (!checked) {
            delete selectedMap[publishId];
        }

        const eligibleCount = this.state.jobs.filter((job) => this.isEligible(job)).length;
        this.setState({
            selectedMap,
            isSelectAll: eligibleCount > 0 && Object.keys(selectedMap).length === eligibleCount
        });
    }

    showSummaryToast = (summary) => {
        toast.info(`Queued: ${summary.queued || 0}, Skipped: ${summary.skipped || 0}, Failed: ${summary.failed || 0}`);
    }

    onReindexSingle = async (publishId) => {
        const rowLoading = {
            ...this.state.rowLoading,
            [publishId]: true
        };
        this.setState({ rowLoading });

        try {
            const response = await reindexJobs(this.state.adminId, {
                publishIds: [publishId]
            });
            const data = response.data || {};
            this.showSummaryToast(data.summary || {});
            const [rowResult] = data.results || [];
            if (rowResult && rowResult.reason && rowResult.status !== 'queued') {
                toast.warn(rowResult.reason);
            }
        } catch (error) {
            toast.error('Failed to enqueue reindex event');
        } finally {
            const updatedRowLoading = {
                ...this.state.rowLoading,
                [publishId]: false
            };
            this.setState({ rowLoading: updatedRowLoading });
        }
    }

    onReindexBulk = async () => {
        const publishIds = Object.keys(this.state.selectedMap)
            .filter((id) => this.state.selectedMap[id])
            .map((id) => Number(id));

        if (!publishIds.length) {
            toast.warn('Please select at least one eligible job');
            return;
        }

        this.setState({ bulkLoading: true });
        try {
            const response = await reindexJobs(this.state.adminId, { publishIds });
            const data = response.data || {};
            this.showSummaryToast(data.summary || {});
            this.setState({
                selectedMap: {},
                isSelectAll: false
            });
        } catch (error) {
            toast.error('Failed to enqueue bulk reindex events');
        } finally {
            this.setState({ bulkLoading: false });
        }
    }

    render() {
        const selectedCount = Object.keys(this.state.selectedMap).filter((id) => this.state.selectedMap[id]).length;
        return (
            <div className="jobs-reindex-page">
                <ToastContainer
                    position="bottom-left"
                    autoClose={5000}
                    hideProgressBar
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnVisibilityChange
                    draggable={false}
                    pauseOnHover
                />

                <div className="page-heading">
                    <Heading text="Jobs Reindex Management" />
                </div>

                <div className="filters-row">
                    <InputBox name="recruiterId" placeholder="Recruiter ID" value={this.state.recruiterId} onChange={this.onChange} />
                    <InputBox name="companyId" placeholder="Verified Company ID" value={this.state.companyId} onChange={this.onChange} />
                    <InputBox name="publishId" placeholder="Publish ID" value={this.state.publishId} onChange={this.onChange} />
                    <InputBox name="domain" placeholder="Company Domain" value={this.state.domain} onChange={this.onChange} />
                    <Button skin="dark" text="Search" clickButton={this.onSearch} />
                    <Button skin="light" text="Clear" clickButton={this.onClear} />
                </div>

                <div className="selection-row">
                    <label className="select-all-label">
                        <input
                            type="checkbox"
                            checked={this.state.isSelectAll}
                            onChange={this.onToggleSelectAll}
                        />
                        Select All Eligible
                    </label>
                    <Button
                        skin="dark"
                        text={`Bulk Reindex (${selectedCount})`}
                        loading={this.state.bulkLoading}
                        disabled={!selectedCount || this.state.bulkLoading}
                        clickButton={this.onReindexBulk}
                    />
                </div>

                <div className="row text-center">
                    <Pagination
                        totalItemsCount={this.state.totalCount}
                        content={this.state.pageContent}
                        pageNumber={this.state.pageNumber}
                        handlePageChange={this.onPageChange}
                    />
                    <CountDisplay
                        start={(this.state.pageNumber - 1) * this.state.pageContent + 1}
                        end={(this.state.pageNumber - 1) * this.state.pageContent + this.state.jobs.length}
                        total={this.state.totalCount}
                    />
                </div>

                {this.state.isLoading ? <Loader /> : (
                    <Table fixed selectable className="jobs-reindex-table">
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Select</Table.HeaderCell>
                                <Table.HeaderCell>Job ID</Table.HeaderCell>
                                <Table.HeaderCell>Job Title</Table.HeaderCell>
                                <Table.HeaderCell>Published Date</Table.HeaderCell>
                                <Table.HeaderCell>Action</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {this.state.jobs.map((job) => {
                                const eligible = this.isEligible(job);
                                const disabledReason = 'Older than 5 months; reindex not allowed';
                                return (
                                    <Table.Row key={job.publishId}>
                                        <Table.Cell>
                                            <input
                                                type="checkbox"
                                                checked={!!this.state.selectedMap[job.publishId]}
                                                disabled={!eligible}
                                                onChange={(event) => this.onToggleSelection(job.publishId, event.target.checked)}
                                            />
                                        </Table.Cell>
                                        <Table.Cell>{job.publishId}</Table.Cell>
                                        <Table.Cell>{job.title}</Table.Cell>
                                        <Table.Cell>{job.publishedDate ? moment(job.publishedDate).format('DD MMM YYYY') : '-'}</Table.Cell>
                                        <Table.Cell>
                                            {eligible ? (
                                                <Button
                                                    skin="dark"
                                                    text="Reindex"
                                                    loading={this.state.rowLoading[job.publishId]}
                                                    disabled={!!this.state.rowLoading[job.publishId]}
                                                    clickButton={() => this.onReindexSingle(job.publishId)}
                                                />
                                            ) : (
                                                <span title={disabledReason}>
                                                    <Button skin="light" text="Reindex" disabled={true} />
                                                </span>
                                            )}
                                        </Table.Cell>
                                    </Table.Row>
                                );
                            })}
                        </Table.Body>
                    </Table>
                )}
            </div>
        );
    }
}

export default JobsReindex;
